import React, {useEffect, useMemo, useRef, useState} from 'react';
import useRefOffsetDimensions from '../../hooks/useRefOffsetDimensions';
import {Grid, Paper, Typography} from '@mui/material';
import PlacesAutoCompleteMuiItem from "./PlacesAutoCompleteMuiItem";
import {useMapsLibrary} from "@vis.gl/react-google-maps";
import {PlacePrediction} from "./interfaces";
import {styled} from "@mui/material/styles";
import AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken;
import AutocompleteSuggestion = google.maps.places.AutocompleteSuggestion;

interface PlacesMuiContainerProps {
  width: number | string;
  height: number;
  left: number | string;
  top: number;
}

const PlacesMuiContainer = styled(Paper)(({width, height, left, top}: PlacesMuiContainerProps) => ({
  position: 'absolute',
  width: width,
  left: left,
  top: top + height * .75,
  boxShadow: '0 .1428em .0714em -.0714em rgb(0 0 0 / 20%), 0px .0714em .0714em 0 rgb(0 0 0 / 14%), 0 .0714em .21428em 0 rgb(0 0 0 / 12%)',
  borderRadius: '.285714em',
  color: 'rgba(0, 0, 0, 0.87)',
  transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  backgroundColor: '#fff',
  zIndex: 10002,
}));

interface PlacesMuiMarginsProps {
  addMargins?: boolean;
}

const PlacesMuiMargins = styled(Grid)(({addMargins}: PlacesMuiMarginsProps) => ({
  ...(addMargins && {
      '&:first-child': {
        marginTop: '.5em',
      },
      '&:last-child': {
        marginBottom: '1.5em',
      }
  })
}));

const DEFAULT_INCLUDED_PRIMARY_TYPES = ['street_address', 'locality'];

const PlacesAutocompleteMui = (props: {
  inputRef: any;
  inputValue: any;
  containerRef?: any;
  onClickPac: (pacItem: PlacePrediction) => void;
  handleSelectPac: (pacItem: PlacePrediction | undefined) => void;
  selectedPacItem: any;
  /** Google Places primary types to include (e.g. ['locality'] for US cities only). */
  includedPrimaryTypes?: string[];
}) => {
  const {
    inputRef,
    inputValue,
    containerRef = inputRef,
    onClickPac,
    handleSelectPac,
    selectedPacItem,
    includedPrimaryTypes = DEFAULT_INCLUDED_PRIMARY_TYPES,
  } = props;

  // Stable session token for this autocomplete session.
  // Created once per mount; when showPac toggles false→true a fresh mount (and token) begins.
  const pacSessionRef = useRef<AutocompleteSessionToken | null>(null);
  if (!pacSessionRef.current) {
    pacSessionRef.current = new window.google.maps.places.AutocompleteSessionToken();
  }
  const pacSession = pacSessionRef.current;
  const {width, left, top, height} = useRefOffsetDimensions(containerRef);
  const [autocompleteResults, setAutocompleteResults] = useState<Array<PlacePrediction>>([]);

  const placesLibrary = useMapsLibrary('places');
  const AutocompleteSuggestion = useMemo(() => placesLibrary?.AutocompleteSuggestion, [placesLibrary]);

  useEffect(() => {
    let isRendered = true;
    const debounceTimeout = setTimeout(() => {
      const getPlacesAutocompleteSuggestions = async () => {
        try {
          // Don't proceed if the service isn't available yet
          if (!AutocompleteSuggestion || !placesLibrary || !inputValue || !pacSession) {
            setAutocompleteResults([]);
            return;
          }

          // Use the service to get predictions
          const request = {
            input: inputValue,
            sessionToken: pacSession,
            language: "en-US",
            region: "us",
            includedRegionCodes: ["us"], // Restrict to US addresses only
            includedPrimaryTypes // Caller-configurable (defaults to street_address + locality)
          };

          const predictions: {suggestions:Array<AutocompleteSuggestion>} = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

          if (!isRendered) return;

          // Format results to match expected structure
          const formattedResults = predictions.suggestions.map((prediction: AutocompleteSuggestion) => {
            const place = prediction.placePrediction;
            if (!place) return null;
              return {
                place_id: place.placeId,
                structured_formatting: {
                  main_text: place.mainText?.text || '',
                  secondary_text: place.secondaryText?.text || '',
                  full_address: place.types.includes("street_address") ? place.text?.text : ''
                },
                description: place.text.text
              }
            });

          setAutocompleteResults(formattedResults.filter(result => result !== null));
        } catch (error) {
          console.error("Error fetching autocomplete suggestions:", error);
          if (isRendered) {
            setAutocompleteResults([]);
          }
        }
      };

      if (inputValue && pacSession) {
        getPlacesAutocompleteSuggestions();
      } else {
        setAutocompleteResults([]);
      }
    }, 300);

    return () => {
      isRendered = false;
      clearTimeout(debounceTimeout);
    };
  }, [inputValue, AutocompleteSuggestion, placesLibrary, pacSession, includedPrimaryTypes]);

  useEffect(() => {
    const ENTER = 'Enter';
    const UP = 'ArrowUp';
    const DOWN = 'ArrowDown';

    const handleUpDown = (event: KeyboardEvent) => {
      const isInputFocused = Boolean(inputRef?.current && document.activeElement === inputRef.current);
      if (!isInputFocused) {
        return;
      }

      if (event?.key === DOWN) {
        if (!autocompleteResults.length) return;

        const selectedPlaceId = selectedPacItem?.place_id;
        const pacIndex = selectedPlaceId
          ? autocompleteResults.findIndex((x: PlacePrediction) => x.place_id === selectedPlaceId)
          : -1;

        if (pacIndex !== -1 && pacIndex !== autocompleteResults.length - 1) {
          handleSelectPac(autocompleteResults.at(pacIndex + 1));
        } else {
          handleSelectPac(autocompleteResults.at(0));
        }
      } else if (event?.key === UP) {
        if (!autocompleteResults.length) return;

        const selectedPlaceId = selectedPacItem?.place_id;
        const pacIndex = selectedPlaceId
          ? autocompleteResults.findIndex((x: PlacePrediction) => x.place_id === selectedPlaceId)
          : -1;

        if (pacIndex > 0) {
          handleSelectPac(autocompleteResults.at(pacIndex - 1));
        } else {
          handleSelectPac(autocompleteResults.at(autocompleteResults.length - 1));
        }
      } else if (event?.key === ENTER) {
        if (selectedPacItem?.description) {
          // if an item is selected, enter selects that item
          onClickPac(selectedPacItem);
        } else if (autocompleteResults?.length) {
          // if an item isn't selected, but there is an autocomplete result list, select first item
          const [firstPacItem] = autocompleteResults;
          onClickPac(firstPacItem);
        }
      }
    }

    window.addEventListener('keydown', handleUpDown);

    return () => {
      window.removeEventListener('keydown', handleUpDown);
    }
  }, [autocompleteResults, handleSelectPac, selectedPacItem, onClickPac, inputRef]);

  return <PlacesMuiContainer width={width} height={height} left={left} top={top} elevation={0}>
    <Grid container direction={'row'} alignContent={'stretch'}>
      <PlacesMuiMargins container
                        item
                        direction={'column'}
                        alignContent={'stretch'}
                        rowSpacing={1}
                        addMargins={autocompleteResults?.length > 0}
      >
        {autocompleteResults.length > 0 && <Typography variant={'overline'} style={{
          marginTop: '1.14em',
          marginRight: '2em',
          marginBottom: '0.29em',
          marginLeft: '2em',
        }}>
          popular searches
        </Typography>
        }
        {
          autocompleteResults.map(
            (result) => <PlacesAutoCompleteMuiItem
              key={result.place_id}
              pacItem={result}
              onClick={onClickPac}
              selected={result.place_id === selectedPacItem?.place_id}
            />
          )
        }
      </PlacesMuiMargins>
    </Grid>
  </PlacesMuiContainer>
}

export default PlacesAutocompleteMui;
